import type { Dayjs } from 'dayjs';
import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { toast } from 'sonner';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import { GradeCanvas, colunaAcoes, SX_GRADE_CANVAS } from 'src/auditoria/components/ds';

import { Label } from 'src/components/label';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import {
  type NfseListItem,
  type NfseListResponse,
  formatDateBr,
  formatCurrency,
  getSituacaoLabel,
  getSituacaoColor,
} from './types';

// ----------------------------------------------------------------------
// NFSA-e → Consultar Nota. PORTAL OVERLAY = SOMENTE LEITURA. Grid das notas
// emitidas pelo contribuinte (base da auditoria). Sem Imprimir/Cancelar (fluxos
// de emissão do Maker) — só Detalhes.
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Grade de PRIORIDADE 1 — `VERIFICACAO.md` §5.3.
//
// "Consultar Nota" é vizinha direta de `Guias` e `Conta Fiscal` no menu do portal,
// que já estão no canvas: o contribuinte trocava de item do menu e a tabela mudava
// de tipografia, de altura de linha e de borda no mesmo clique.
//
// Aqui entra o casulo INTEIRO, e não só o `SX_GRADE_CANVAS`, porque a tela tinha
// duas superfícies onde o canvas desenha uma: um `<Card sx={{ p: 2, mb: 3 }}>` só
// com os dois filtros, flutuando acima do `Paper` do `TxDataGrid`. Os filtros
// passam a ser o slot `toolbar.filters` do mesmo card — a ordem do canvas,
// toolbar → grade, dentro de um `.card` só.
//
// O contêiner do casulo é o `DashboardContent`, que paga o gutter da página
// (`--layout-dashboard-content-px`) FORA da borda do card. O respiro de 16px do
// `SX_GRADE_CANVAS` é medido de DENTRO dela: não há recuo duplo a neutralizar e
// nenhum `'& .MuiDataGrid-main': { paddingInline: 0 }` é necessário.
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

// Filtro de Situação — codigo_situacao (texto) da nota_fiscal da auditoria.
const SITUACAO_OPCOES = [
  { value: '', label: 'Todas' },
  { value: 'tp', label: 'Válida' },
  { value: 'ca', label: 'Cancelada' },
] as const;

// ----------------------------------------------------------------------

export default function NfseListPage() {
  const navigate = useNavigate();

  const [competencia, setCompetencia] = useState<Dayjs | null>(null);
  const [situacao, setSituacao] = useState<string>('');

  // Monta a querystring com os filtros ativos (mês/ano da competência + situação).
  const swrKey = useMemo(() => {
    const params = new URLSearchParams();
    if (situacao !== '') params.set('situacao', String(situacao));
    if (competencia) {
      params.set('mes', String(competencia.month() + 1));
      params.set('ano', String(competencia.year()));
    }
    const qs = params.toString();
    return qs ? `/portal-api/nfse?${qs}` : '/portal-api/nfse';
  }, [situacao, competencia]);

  const { data, isLoading } = useSWR<NfseListResponse>(swrKey, fetcher);

  const handleImprimir = async (row: NfseListItem) => {
    try {
      const res = await axiosInstance.get(`/portal-api/nfse/${row.id}/imprimir`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(
        new Blob([res.data as Blob], { type: 'application/pdf' })
      );
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      toast.error('Falha ao gerar o PDF.');
    }
  };

  const columns: GridColDef<NfseListItem>[] = [
    {
      field: 'numero',
      headerName: 'Número',
      flex: 0.8,
      minWidth: 120,
      valueGetter: (_v, row) => row.numero ?? '—',
    },
    {
      field: 'serie',
      headerName: 'Série',
      flex: 0.5,
      minWidth: 90,
      valueGetter: (_v, row) => row.serie || '—',
    },
    {
      field: 'data_emissao',
      headerName: 'Data emissão',
      flex: 0.8,
      minWidth: 130,
      valueGetter: (_v, row) => formatDateBr(row.data_emissao),
    },
    {
      field: 'tomador',
      headerName: 'Tomador',
      flex: 1.4,
      minWidth: 200,
      valueGetter: (_v, row) => row.tomador?.nome ?? '—',
    },
    {
      field: 'valor',
      headerName: 'Valor',
      flex: 0.8,
      minWidth: 130,
      valueGetter: (_v, row) => formatCurrency(row.valor),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      flex: 0.9,
      minWidth: 150,
      renderCell: (params) => (
        <Label color={getSituacaoColor(params.row.situacao)}>
          {getSituacaoLabel(params.row.situacao)}
        </Label>
      ),
    },
    // Coluna de ações — overlay é só leitura: Detalhes + Imprimir (PDF).
    //
    // Era `GridActionsCellItem` com `solar:eye-bold` / `solar:printer-minimalistic-bold`:
    // ícone SÓLIDO, contra §3.4, e 100px em vez dos 96 do artboard. O `colunaAcoes`
    // do kit entrega a mesma dupla em traço de 18px, à direita, com nome acessível
    // por linha. As duas ações continuam as mesmas, e continuam as únicas.
    colunaAcoes<NfseListItem>([
      {
        icon: 'olho',
        label: (row) => `Ver detalhes da nota ${row.numero ?? ''}`.trim(),
        onClick: (row) => navigate(`/nfse/${row.id}`),
      },
      {
        icon: 'imprimir',
        label: (row) => `Imprimir a nota ${row.numero ?? ''}`.trim(),
        onClick: (row) => handleImprimir(row),
      },
    ]),
  ];

  return (
    <DashboardContent>
      {/* PORTE AUDITORIA: o portal overlay não emite nota — botão "Emitir" removido.
          A tela é só consulta das notas emitidas (importadas). */}
      <CustomBreadcrumbs
        heading="Consultar Nota"
        links={[{ name: 'NFS-e', href: '/nfse' }, { name: 'Consultar Nota' }]}
        sx={{ mb: { xs: 2, md: 3 } }}
      />

      <GradeCanvas
        toolbar={{
          // Os mesmos dois controles do `Card` que existia aqui, agora no slot de
          // filtros do casulo: `GradeToolbar` já paga `16px 16px 12px` e envolve em
          // `flexWrap`, então em tela estreita eles quebram de linha sozinhos — era
          // o que o `flexDirection: { xs: 'column' }` do card fazia à mão.
          filters: (
            <>
              <DatePicker
                label="Competência"
                views={['year', 'month']}
                openTo="month"
                format="MM/YYYY"
                value={competencia}
                onChange={(v) => setCompetencia(v)}
                slotProps={{
                  textField: { size: 'small', sx: { minWidth: 180 } },
                  field: { clearable: true },
                }}
              />

              <TextField
                select
                size="small"
                label="Situação"
                value={situacao}
                onChange={(e) => setSituacao(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {SITUACAO_OPCOES.map((opt) => (
                  <MenuItem key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ),
        }}
      >
        {/*
          `onView` saiu: era ele que fazia o `formatColumns` do portal injetar uma
          SEGUNDA coluna de ações ao lado da declarada acima. O clique em "Detalhes"
          continua, agora no `colunaAcoes`, para a mesma rota.
        */}
        <TxDataGrid<NfseListItem>
          rows={data?.notas ?? []}
          columns={columns}
          loading={isLoading}
          emptyLabel="Nenhuma nota encontrada"
          {...PROPS_GRADE}
          sx={SX_GRADE}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
